import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import storageSession from "redux-persist/es/storage/session";
import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import { setNavigatedByBusiness } from "./businessSelector";

interface TreeViewContextState {
  expandedNodes: string[]; // "${aff}-${abo}" keys, unpadded
}

interface TreeViewUiState {
  byContext: Record<string, TreeViewContextState>;
}

const initialState: TreeViewUiState = { byContext: {} };

const { actions, reducer } = createSlice({
  name: "treeViewUiState",
  initialState,
  reducers: {
    toggleExpandedNode: (state, action: PayloadAction<{ contextKey: string; nodeId: string; expanded: boolean }>) => {
      const { contextKey, nodeId, expanded } = action.payload;
      const existing = state.byContext[contextKey]?.expandedNodes ?? [];
      const set = new Set(existing);
      if (expanded) set.add(nodeId);
      else set.delete(nodeId);
      state.byContext[contextKey] = { expandedNodes: [...set] };
    },
    setExpandedNodes: (state, action: PayloadAction<{ contextKey: string; nodeIds: string[] }>) => {
      const { contextKey, nodeIds } = action.payload;
      state.byContext[contextKey] = { expandedNodes: nodeIds };
    },
    clearTreeViewState: (state, action: PayloadAction<{ contextKey?: string }>) => {
      const { contextKey } = action.payload;
      if (contextKey) delete state.byContext[contextKey];
      else state.byContext = {};
    },
  },
  extraReducers: (builder) => {
    // Wipe all cached expansion when the user switches business so stale node IDs
    // don't get applied to a different tree.
    builder.addCase(setNavigatedByBusiness, (state, action) => {
      if (action.payload === true) {
        state.byContext = {};
      }
    });
  },
});

export const { toggleExpandedNode, setExpandedNodes, clearTreeViewState } = actions;

const persistConfig = {
  key: "treeViewUiState",
  storage: storageSession,
  stateReconciler: autoMergeLevel2,
};

export default persistReducer<TreeViewUiState>(persistConfig, reducer);
